import React, { useState, useRef } from 'react';
import styles from '../main.module.css';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const ImageUploadModal = ({ 
    isVisible, 
    onClose, 
    onConvert, 
    isConverting 
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            console.log('Selected image file:', {
                name: file.name,
                type: file.type,
                size: file.size
            });
            setSelectedFile(file);
            // Create preview URL
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
        }
    };

    const handleConvert = () => {
        if (selectedFile) {
            onConvert(selectedFile);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            const validImageTypes = [
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/gif',
                'image/webp',
                'image/bmp',
                'image/tiff'
            ];

            // Also check file extension as fallback
            const fileName = file.name.toLowerCase();
            const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff'];
            const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

            if (validImageTypes.includes(file.type) || hasValidExtension) {
                setSelectedFile(file);
                const url = URL.createObjectURL(file);
                setPreviewUrl(url);
            }
        }
    };

    if (!isVisible) return null;

    return (
        <div className={styles.modalBackdrop} onClick={handleClose}>
            <div className={styles.modalContentWrapper} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3><i className="fas fa-file-image mr-2"></i>Upload Image File</h3>
                    <button onClick={handleClose} className={styles.modalCloseBtn}>×</button>
                </div>
                
                <div className={styles.modalBody}>
                    <div className={styles.uploadSection}>
                        <div 
                            className={styles.fileDropZone}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {previewUrl ? (
                                <div className={styles.imagePreview}>
                                    <img 
                                        src={previewUrl} 
                                        alt="Preview" 
                                        className={styles.previewImage}
                                    />
                                    <div className={styles.uploadOverlay}>
                                        <p>Click to change image</p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className={styles.uploadIcon}>
                                        <i className="fas fa-cloud-upload-alt"></i>
                                    </div>
                                    <div className={styles.uploadText}>
                                        <p><strong>Choose an image file or drag it here</strong></p>
                                        <p className={styles.uploadSubtext}>
                                            Supported formats: JPEG, PNG, GIF, WebP, BMP, TIFF (Max 20MB)
                                        </p>
                                    </div>
                                </>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                        </div>
                        
                        {selectedFile && (
                            <div className={styles.selectedFile}>
                                <div className={styles.fileInfo}>
                                    <i className="fas fa-file-image"></i>
                                    <div className={styles.fileDetails}>
                                        <span className={styles.fileName}>{selectedFile.name}</span>
                                        <span className={styles.fileSize}>
                                            {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                                        </span>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => {
                                        setSelectedFile(null);
                                        if (previewUrl) {
                                            URL.revokeObjectURL(previewUrl);
                                            setPreviewUrl(null);
                                        }
                                        if (fileInputRef.current) fileInputRef.current.value = '';
                                    }}
                                    className={styles.removeFileButton}
                                    title="Remove file"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                
                <div className={styles.modalFooter}>
                    <button
                        onClick={handleClose}
                        className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
                        disabled={isConverting}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConvert}
                        className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
                        disabled={!selectedFile || isConverting}
                    >
                        {isConverting ? <LoadingSpinner small centered={false} /> : 'Extract Text'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageUploadModal;
